import { apiFetch, apiNoContentTypeFetch, type CustomFetchOptions } from "./index";
import { getApiBaseUrl } from "./baseUrl";

const API_BASE = getApiBaseUrl();

export type DailyCoachStyle = "gentle" | "normal" | "strict";
export type DailyCoachAvatarKey = "female" | "male";
export type DailyCoachLanguage = "ja" | "en";

export interface DailyCoach {
  name: string;
  avatar_key?: DailyCoachAvatarKey;
  avatar_url?: string | null;
  style: DailyCoachStyle;
}

export interface DailyCoachSettingsResponse {
  coach: DailyCoach;
  is_initialized: boolean;
}

export interface DailyCoachTaskDraft {
  title: string;
  category?: string | null;
  detail?: string | null;
  memo?: string | null;
  priority?: number | null;
  estimated_minutes?: number | null;
  estimated_duration?: string | null;
  source_week_task_id?: number | null;
  source_week_task_title?: string | null;
}

export interface DailyCoachTaskProposal {
  target_date: string;
  available_minutes: number;
  has_weekly_tasks?: boolean;
  has_goal_context?: boolean;
  tasks: DailyCoachTaskDraft[];
}

export interface DailyCoachChatResponse {
  session_id: string;
  /** 未来ボードのカードから来た提案のとき、そのカードの ID */
  future_card_id?: number | null;
  coach: DailyCoach;
  message: string;
  task_proposal?: DailyCoachTaskProposal | null;
}

export interface DailyCoachChatJobResponse {
  job_id: string;
  session_id: string;
}

export interface DailyCoachChatJobStatus {
  status: "pending" | "completed";
  response?: DailyCoachChatResponse;
}

type DailyCoachRequestOptions = Pick<CustomFetchOptions, "hideGlobalLoader">;

function withAuthorization(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

function withLanguage(path: string, language: DailyCoachLanguage = "ja"): string {
  return `${path}?language=${encodeURIComponent(language)}`;
}

export function getDailyCoachSettings(
  token: string,
  language: DailyCoachLanguage = "ja"
): Promise<DailyCoachSettingsResponse> {
  return apiFetch(withLanguage(`${API_BASE}/api/management/daily-coach/settings`, language), {
    headers: withAuthorization(token),
  });
}

export function updateDailyCoachSettings(
  token: string,
  coach: Partial<Pick<DailyCoach, "name" | "avatar_key" | "style">>,
  language: DailyCoachLanguage = "ja"
): Promise<{ coach: DailyCoach }> {
  return apiFetch(withLanguage(`${API_BASE}/api/management/daily-coach/settings`, language), {
    method: "PATCH",
    headers: { ...withAuthorization(token), "Content-Type": "application/json" },
    body: JSON.stringify(coach),
  });
}

export function uploadDailyCoachAvatar(
  token: string,
  file: any,
  language: DailyCoachLanguage = "ja"
): Promise<{ coach: DailyCoach }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiNoContentTypeFetch(withLanguage(`${API_BASE}/api/management/daily-coach/avatar`, language), {
    method: "POST",
    headers: withAuthorization(token),
    body: formData,
  });
}

// 独自の画像を削除し、標準のアバターに戻す。
export function deleteDailyCoachAvatar(
  token: string,
  language: DailyCoachLanguage = "ja"
): Promise<{ coach: DailyCoach }> {
  return apiFetch(withLanguage(`${API_BASE}/api/management/daily-coach/avatar`, language), {
    method: "DELETE",
    headers: withAuthorization(token),
  });
}

export function chatWithDailyCoach(
  token: string,
  payload: {
    message: string;
    target_date: string;
    available_minutes: number;
    session_id?: string;
    language?: DailyCoachLanguage;
    async?: boolean;
    /** 未来ボードのカードから開いたとき。サーバーが、カードの最新の保存版と関連ゴールを使う */
    future_card_id?: number;
  },
  options: DailyCoachRequestOptions = {},
): Promise<DailyCoachChatJobResponse> {
  return apiFetch(`${API_BASE}/api/management/daily-coach/chat`, {
    method: "POST",
    headers: { ...withAuthorization(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    hideGlobalLoader: options.hideGlobalLoader,
  });
}

export function createDailyCoachTasks(
  token: string,
  payload: {
    target_date: string;
    available_minutes: number;
    tasks: DailyCoachTaskDraft[];
    create_as_unlinked?: boolean;
    language?: DailyCoachLanguage;
  }
): Promise<{ tasks: unknown[] }> {
  return apiFetch(`${API_BASE}/api/management/daily-coach/tasks`, {
    method: "POST",
    headers: { ...withAuthorization(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getDailyCoachChatJob(
  token: string,
  jobId: string,
  language: DailyCoachLanguage = "ja",
  options: DailyCoachRequestOptions = {},
): Promise<DailyCoachChatJobStatus> {
  return apiFetch(withLanguage(`${API_BASE}/api/management/daily-coach/chat/${encodeURIComponent(jobId)}`, language), {
    headers: withAuthorization(token),
    hideGlobalLoader: options.hideGlobalLoader,
  });
}

export async function fetchDailyCoachVoice(
  token: string,
  text: string,
  language: DailyCoachLanguage = "ja",
  avatarKey?: DailyCoachAvatarKey,
): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/management/daily-coach/voice`, {
    method: "POST",
    headers: { ...withAuthorization(token), "Content-Type": "application/json" },
    body: JSON.stringify({ text, language, avatar_key: avatarKey }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    let apiError = "";
    try {
      const payload = JSON.parse(body);
      apiError = typeof payload?.error === "string" ? payload.error : "";
    } catch {
      // JSON形式でないエラー本文は、下の汎用エラーとして扱います。
    }
    if (apiError) throw new Error(apiError);
    throw new Error(`API error: ${response.status} ${body}`);
  }

  return response.blob();
}
